import { makeAutoObservable, runInAction } from 'mobx';
import { debounce, isArray, logger } from '@cutwise/utils';

import useStore from '@/common/hooks/useStore';
import { EVENT_KEYBOARD, EVENT_LOOSE_FOCUS } from '@/stereo/constants/events';

type EventType = 'message' | 'close' | 'keydown';

interface CallbackDataType {
  message: SeparateWindowMessage;
  keydown: KeyboardEvent;
  close?: Any;
}

type EventCallback = (a: CallbackDataType[EventType]) => void;

interface SeparateWindowMessage {
  action: string;
  data: Any;
}

interface OpenConfig {
  sameScreen?: boolean;
}

class SeparateWindowStore {
  isOpen = false;

  private listeners: Record<EventType, EventCallback[]> = {
    message: [],
    keydown: [],
    close: [],
  };

  private closeInterval?: SetInterval;

  constructor() {
    makeAutoObservable<SeparateWindowStore, 'listeners' | 'closeInterval' | 'executeListener'>(this, {
      listeners: false,
      closeInterval: false,
      isParent: false,
      isChild: false,
      href: false,
      executeListener: false,
    });

    this.onMessage = this.onMessage.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.subscribeToEvents();
  }

  get isParent() {
    return Boolean(window.SEPARATE_WINDOW);
  }

  get isChild() {
    return !this.isParent;
  }

  get href() {
    if (this.isParent) {
      return window.SEPARATE_WINDOW?.location.href;
    }

    return window.opener?.location.href;
  }

  async open(path: string, config: OpenConfig = {}) {
    const { sameScreen = true } = config;

    if (this.isOpen) {
      if (window.SEPARATE_WINDOW) {
        window.SEPARATE_WINDOW.location.href = path;
      }

      return;
    }

    let features = 'popup';

    if (!sameScreen) {
      try {
        if ('getScreenDetails' in window && window.screen.isExtended) {
          let targetScreen = window.screen as unknown as ScreenDetailed;

          const { state } = await navigator.permissions.query({
            name: 'window-management' as unknown as PermissionName,
          });

          if (state === 'granted') {
            const screens = await window.getScreenDetails();
            const secondScreen = screens.screens.find((screen) => screen !== screens.currentScreen);

            if (secondScreen) {
              targetScreen = secondScreen;
            }

            features = [
              `left=${targetScreen.availLeft}`,
              `top=${targetScreen.availTop}`,
              `width=${targetScreen.availWidth}`,
              `height=${targetScreen.availHeight}`,
              'menubar=no',
              'toolbar=no',
              'location=no',
              'status=no',
              'resizable=yes',
              'scrollbars=no',
            ].join(',');
          }
        }
      } catch (e) {
        logger.warn(e.message, e);
      }
    }

    window.SEPARATE_WINDOW = window.open(path, '_blank', features);

    if (window.SEPARATE_WINDOW) {
      window.SEPARATE_WINDOW.focus();

      /*
       * If "window.SEPARATE_WINDOW" is presented it means that child window is opened,
       * in this case check that child window is still opened
       * */
      clearInterval(this.closeInterval);
      runInAction(() => {
        this.closeInterval = setInterval(() => {
          if (window.SEPARATE_WINDOW?.closed) {
            this.executeListener('close');
            this.close(); // execute handlers if child window is closed
          }
        }, 300);
      });
    }

    runInAction(() => {
      this.isOpen = true;
    });
  }

  close() {
    if (this.isParent) {
      window.SEPARATE_WINDOW!.close();
      window.SEPARATE_WINDOW!.onmessage = null;
      window.SEPARATE_WINDOW = null;

      clearInterval(this.closeInterval);

      this.isOpen = false;

      return;
    }

    window.close();
  }

  on<T extends EventType>(event: T, callback: (a: CallbackDataType[T]) => void) {
    if (isArray(this.listeners?.[event])) {
      this.listeners[event].push(callback);
    } else {
      this.listeners[event] = [callback];
    }
  }

  off(event: EventType, callback: EventCallback) {
    if (isArray(this.listeners?.[event])) {
      const index = this.listeners?.[event].findIndex((func: EventCallback) => func.toString() === callback.toString());

      if (index !== -1) {
        this.listeners[event].splice(index, 1);
      }
    }
  }

  send(action: string, data?: Any) {
    const message = JSON.stringify({ action, data });

    if (this.isParent) {
      window.SEPARATE_WINDOW?.postMessage(message);
    } else {
      window.opener?.postMessage(message);
    }
  }

  looseFocus() {
    this.send(EVENT_LOOSE_FOCUS);
  }

  private onMessage(e: MessageEvent) {
    if (e.origin !== window.location.origin) {
      return;
    }

    this.executeListener('message', e);
  }

  private executeListener = debounce((event: EventType, data: Nullable<Any> = null) => {
    if (!isArray(this.listeners?.[event])) {
      return;
    }

    this.listeners[event].forEach((callback) => {
      if (event === 'message' && data.data) {
        if (typeof data.data === 'string') {
          const parsedData = JSON.parse(data.data);

          callback?.(parsedData);
        } else {
          callback?.(data.data);
        }

        return;
      }

      callback?.(data);
    });
  }, 0);

  private subscribeToEvents() {
    if (!this.isParent) {
      try {
        // TODO add check that "window.opener" has same origin as current window
        // when parent window closed or reloaded
        window.opener?.addEventListener('unload', this.close);
      } catch (e) {
        logger.error(e.message);
      }
    }

    window.addEventListener('message', this.onMessage);
    window.addEventListener('keydown', this.onKeyDown);
  }

  /*
   * Transfer keyboard events to parent window
   * */
  private onKeyDown(e: KeyboardEvent) {
    if (this.isParent) {
      return;
    }

    switch (e.key) {
      case 'Escape':
        this.close();

        break;
      default:
        this.send(EVENT_KEYBOARD, {
          key: e.key,
          ctrlKey: e.ctrlKey,
          code: e.code,
          shiftKey: e.shiftKey,
          altKey: e.altKey,
          metaKey: e.metaKey,
        });

        break;
    }
  }
}

export const useSeparateWindowStore = () => useStore(SeparateWindowStore);

const separateWindowStore = new SeparateWindowStore();

export { SeparateWindowStore };
export type { SeparateWindowMessage };
export default separateWindowStore;
